import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateFormData } from '../features/FormRegistration';
import { getHospitalFeatureIcon } from '../utils/helpers';
import { Input, Loader, Tooltip } from '@mantine/core';
import SimpleInfoIcon from '../icons/SimpleInfoIcon';
import CheckIcon from '../icons/CheckIcon';

const PRIORITY_HOSPITAL = [
  'Joint Reconstructions',
  'Chemotherapy, Radiotherapy and Immunotherapy for Cancer',
  'Bone, Joint and Muscle',
  'Heart and Vascular System',
  'Rehabilitation',
  'Back, Neck and Spine',
  'Cataracts',
  'Joint Replacements',
];

const getHospitalDisplayName = (name: string) =>
  name === 'Chemotherapy, Radiotherapy and Immunotherapy for Cancer'
    ? 'Chemotherapy'
    : name;

function FeatureSelectionHospital() {
  const [showAllHospitalFeatures, setShowAllHospitalFeatures] = useState(false);

  const toggleShowMoreHospitalFeatures = () => {
    setShowAllHospitalFeatures(!showAllHospitalFeatures);
  };

  const [hospitalCoverFeatures, setHospitalCoverFeatures] = useState<any>([]);
  const hospitalFeaturesToShow = showAllHospitalFeatures
    ? hospitalCoverFeatures
    : hospitalCoverFeatures.slice(0, 8);

  const dispatch = useDispatch();
  const { formData, benefits, isBenefitsLoading } = useSelector(
    (state: any) => state.formRegistration,
  );
  const { isMobileView } = useSelector((state: any) => state.viewport);

  useEffect(() => {
    const hospitalBenefits = benefits.filter(
      (feature: any) => feature.benefit_type === 'hospital',
    );
    hospitalBenefits.sort((a: any, b: any) => {
      const indexA = PRIORITY_HOSPITAL.indexOf(a.benefit_name);
      const indexB = PRIORITY_HOSPITAL.indexOf(b.benefit_name);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return 0;
    });
    setHospitalCoverFeatures(hospitalBenefits);
  }, [benefits]);

  if (isMobileView) {
    return (
      <Input.Wrapper
        label="What hospital services do you want covered?"
        description="Please select the services you require on your health insurance policy"
        className={`flex flex-col w-full gap-[15px] text-start`}>
        {isBenefitsLoading && <Loader />}
        {hospitalFeaturesToShow.map((feature: any) => {
          const isSelected = formData.selectedHospitalBenefits.includes(
            feature.benefit_id,
          );
          return (
            <div
              key={feature.benefit_id}
              className={`border gap-[10px] relative flex font-normal w-full rounded-[8px] text-start text-[16px] cursor-pointer items-center px-[14px] py-[10px] min-h-[44px] border-[#A2AFBE]`}
              style={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                background: isSelected ? '#17385D' : '',
                color: isSelected ? 'white' : '',
              }}
              onClick={() => {
                dispatch(
                  updateFormData({
                    ...formData,
                    selectedHospitalBenefits: isSelected
                      ? formData.selectedHospitalBenefits.filter(
                          (id: number) => id !== feature.benefit_id,
                        )
                      : [
                          ...formData.selectedHospitalBenefits,
                          feature.benefit_id,
                        ],
                  }),
                );
              }}>
              {isSelected ? (
                <div className="border flex items-center justify-center bg-[white] border-[#A2AFBE] rounded-[5px] h-[20px] w-[20px] flex-shrink-0">
                  <CheckIcon />
                </div>
              ) : (
                <div className="border border-[#A2AFBE] rounded-[5px] h-[20px] w-[20px] flex-shrink-0"></div>
              )}{' '}
              <div className="w-[25px]">
                {React.cloneElement(
                  getHospitalFeatureIcon(feature.benefit_name),
                  {
                    fillColor: isSelected ? 'white' : undefined,
                  },
                )}
              </div>
              <div className="w-full">{getHospitalDisplayName(feature.benefit_name)}</div>
              <Tooltip
                key={feature?.benefit_id}
                multiline
                label={feature?.benefit_description}
                classNames={{
                  tooltip:
                    'whitespace-pre-wrap max-w-[90%]  md:max-w-[30%] text-[14px]',
                }}
                position="bottom">
                <div className="absolute right-1 top-[20%] z-[10]">
                  <SimpleInfoIcon />
                </div>
              </Tooltip>
            </div>
          );
        })}
        <span
          className="cursor-pointer text-[16px] font-medium mt-[25px]"
          onClick={toggleShowMoreHospitalFeatures}>
          {showAllHospitalFeatures ? 'Show less -' : 'Show more +'}
        </span>
      </Input.Wrapper>
    );
  }

  return (
    <Input.Wrapper
      label="What hospital services do you want covered?"
      description="Please select the services you require on your health insurance policy"
      className={`flex flex-col w-full gap-[15px] text-start`}>
      {isBenefitsLoading && <Loader />}
      <div className="flex flex-col items-center">
        <div className="grid grid-cols-2 gap-[15px] w-full">
          {hospitalFeaturesToShow.map((feature: any) => {
            const isSelected = formData.selectedHospitalBenefits.includes(
              feature.benefit_id,
            );
            return (
              <div
                key={feature.benefit_id}
                className={`border gap-[10px] relative flex font-normal w-full rounded-[8px] text-start text-[16px] cursor-pointer items-center px-[14px] py-[10px] min-h-[44px] border-[#A2AFBE]`}
                style={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  background: isSelected ? '#17385D' : '',
                  color: isSelected ? 'white' : '',
                }}
                onClick={() => {
                  dispatch(
                    updateFormData({
                      ...formData,
                      selectedHospitalBenefits: isSelected
                        ? formData.selectedHospitalBenefits.filter(
                            (id: number) => id !== feature.benefit_id,
                          )
                        : [
                            ...formData.selectedHospitalBenefits,
                            feature.benefit_id,
                          ],
                    }),
                  );
                }}>
                {isSelected ? (
                  <div className="border flex items-center justify-center bg-[white] border-[#A2AFBE] rounded-[5px] h-[20px] w-[20px] flex-shrink-0">
                    <CheckIcon />
                  </div>
                ) : (
                  <div className="border border-[#A2AFBE] rounded-[5px] h-[20px] w-[20px] flex-shrink-0"></div>
                )}{' '}
                <div className="w-[25px]">
                  {React.cloneElement(
                    getHospitalFeatureIcon(feature.benefit_name),
                    {
                      fillColor: isSelected ? 'white' : undefined,
                    },
                  )}
                </div>
                <div className="w-full">{getHospitalDisplayName(feature.benefit_name)}</div>
                <Tooltip
                  key={feature?.benefit_id}
                  multiline
                  label={feature?.benefit_description}
                  classNames={{
                    tooltip:
                      'whitespace-pre-wrap max-w-[90%]  md:max-w-[30%] text-[14px]',
                  }}
                  position="bottom">
                  <div className="absolute right-1 top-[20%] z-[10]">
                    <SimpleInfoIcon />
                  </div>
                </Tooltip>
              </div>
            );
          })}
        </div>
        <span
          className="cursor-pointer text-[16px] font-medium mt-[25px] underline"
          onClick={toggleShowMoreHospitalFeatures}>
          {showAllHospitalFeatures ? 'Show less -' : 'Show more +'}
        </span>
      </div>
    </Input.Wrapper>
  );
}

export default FeatureSelectionHospital;
